import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
    Users, Plus, Search, MoreHorizontal, Edit2, Trash2, Mail,
    ChevronLeft, ChevronRight, X, AlertCircle,
    CheckCircle, Loader2, Calendar
} from 'lucide-react';
import PageShell from './PageShell';
import TeamMemberWizard from './TeamMemberWizard';
import {
    fetchStaffList,
    fetchStaff,
    deleteStaff,
    updateStaff,
    resendWelcomeEmail,
    fetchStaffRoles,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    openLeavePlanModal,
    closeLeavePlanModal,
    fetchStaffLeavePlans,
    createStaffLeavePlan,
    deleteStaffLeavePlan,
    setFilters,
    setPage,
    clearError,
    clearOperationSuccess,
    selectStaffList,
    selectIsLoading,
    selectError,
    selectPagination,
    selectFilters,
    selectSelectedStaff,
    selectIsLoadingStaff,
    selectIsCreateModalOpen,
    selectIsEditModalOpen,
    selectIsDeleteModalOpen,
    selectIsLeavePlanModalOpen,
    selectLeavePlans,
    selectIsLoadingLeavePlans,
    selectIsSavingLeavePlan,
    selectIsDeletingLeavePlan,
    selectStaffToDelete,
    selectIsDeleting,
    selectIsRefreshing,
    selectOperationError,
    selectOperationSuccess,
} from '../store/slices/staffSlice';

// ============================================
// Delete Confirmation Modal
// ============================================

const DeleteConfirmModal = ({ isOpen, onClose, staff }) => {
    const dispatch = useAppDispatch();
    const isDeleting = useAppSelector(selectIsDeleting);
    const operationError = useAppSelector(selectOperationError);

    const handleDelete = async () => {
        await dispatch(deleteStaff(staff.uuid));
    };

    if (!isOpen || !staff) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />

                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Deactivate Staff Member</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Are you sure you want to deactivate <strong>{staff.fullName}</strong>?
                        They will no longer be able to log in to the system.
                    </p>

                    {operationError && (
                        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-sm text-red-700">{operationError}</p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                        >
                            {isDeleting && <Loader2 size={16} className="animate-spin" />}
                            Deactivate
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Leave Plan Modal
// ============================================

const LeavePlanModal = ({ isOpen, onClose, staff }) => {
    const dispatch = useAppDispatch();
    const leavePlans = useAppSelector(selectLeavePlans);
    const isLoadingLeavePlans = useAppSelector(selectIsLoadingLeavePlans);
    const isSaving = useAppSelector(selectIsSavingLeavePlan);
    const isDeleting = useAppSelector(selectIsDeletingLeavePlan);
    const operationError = useAppSelector(selectOperationError);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [formError, setFormError] = useState(null);

    // Load leave plans when modal opens
    useEffect(() => {
        if (isOpen && staff?.uuid) {
            dispatch(fetchStaffLeavePlans(staff.uuid));
            setStartDate('');
            setEndDate('');
            setFormError(null);
        }
    }, [isOpen, staff?.uuid, dispatch]);

    const handleSave = async () => {
        setFormError(null);

        if (!startDate) {
            setFormError('Start date is required');
            return;
        }
        if (!endDate) {
            setFormError('End date is required');
            return;
        }
        if (endDate < startDate) {
            setFormError('End date must be on or after the start date');
            return;
        }

        const result = await dispatch(createStaffLeavePlan({
            staffUuid: staff.uuid,
            startDate,
            endDate,
        }));

        if (createStaffLeavePlan.fulfilled.match(result)) {
            // Clear form for next entry
            setStartDate('');
            setEndDate('');
        }
    };

    const handleDelete = async (leavePlanUuid) => {
        await dispatch(deleteStaffLeavePlan({
            staffUuid: staff.uuid,
            leavePlanUuid,
        }));
    };

    if (!isOpen || !staff) return null;

    const formatDate = (iso) => {
        if (!iso) return '';
        // Parse YYYY-MM-DD as local date to avoid UTC offset shifts
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />

                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Calendar size={18} className="text-emerald-600" />
                                Leave Plan
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">{staff.fullName}</p>
                        </div>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Error display */}
                        {(formError || operationError) && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                                <p className="text-sm text-red-700">{formError || operationError}</p>
                            </div>
                        )}

                        {/* New leave plan form */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add Leave</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        min={startDate || undefined}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                                >
                                    {isSaving && <Loader2 size={16} className="animate-spin" />}
                                    Save Leave Plan
                                </button>
                            </div>
                        </div>

                        {/* Existing leave plans list */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                Scheduled Leave {leavePlans.length > 0 && `(${leavePlans.length})`}
                            </h3>

                            {isLoadingLeavePlans ? (
                                <div className="py-8 text-center">
                                    <Loader2 className="animate-spin mx-auto text-gray-400" size={20} />
                                    <p className="mt-2 text-sm text-gray-500">Loading leave plans...</p>
                                </div>
                            ) : leavePlans.length === 0 ? (
                                <div className="py-8 text-center border border-dashed border-gray-200 rounded-xl">
                                    <Calendar className="mx-auto text-gray-300" size={32} />
                                    <p className="mt-2 text-sm text-gray-500">No leave scheduled</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {leavePlans.map((plan) => (
                                        <div
                                            key={plan.uuid}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                                                    <Calendar size={16} className="text-emerald-700" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {formatDate(plan.startDate)} → {formatDate(plan.endDate)}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(plan.uuid)}
                                                disabled={isDeleting}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                title="Delete leave plan"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end rounded-b-2xl">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Staff Row Dropdown Menu
// ============================================

const StaffRowMenu = ({ staff, onEdit, onActivate, onDeactivate, onLeavePlan, onResendEmail }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    // Popup uses fixed positioning so it escapes any ancestor with
    // `overflow-hidden` (the table card has rounded-xl + overflow-hidden,
    // which was clipping the menu off at the bottom). We anchor it to the
    // trigger button's bounding rect and flip upward when there's no room
    // below.
    const MENU_HEIGHT = 200;   // approx — 3 items + divider + 1 toggle item
    const MENU_WIDTH = 192;    // matches w-48

    const openMenu = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const flipUp = spaceBelow < MENU_HEIGHT + 16;
            setCoords({
                top: flipUp ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4,
                // Right-align with the button: button's right edge minus menu width
                left: Math.max(8, rect.right - MENU_WIDTH),
            });
        }
        setIsOpen(true);
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
                <MoreHorizontal size={16} />
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        className="fixed w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        <button
                            onClick={() => { onEdit(staff); setIsOpen(false); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Edit2 size={14} />
                            Edit Details
                        </button>
                        <button
                            onClick={() => { onLeavePlan(staff); setIsOpen(false); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Calendar size={14} />
                            Leave Plan
                        </button>
                        <button
                            onClick={() => { onResendEmail(staff.uuid); setIsOpen(false); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Mail size={14} />
                            Resend Welcome Email
                        </button>
                        <hr className="my-1 border-gray-100" />
                        {staff.active ? (
                            <button
                                onClick={() => { onDeactivate(staff); setIsOpen(false); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                                <Trash2 size={14} />
                                Deactivate
                            </button>
                        ) : (
                            <button
                                onClick={() => { onActivate(staff); setIsOpen(false); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50"
                            >
                                <CheckCircle size={14} />
                                Activate
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

// ============================================
// Main TeamPage Component
// ============================================

const TeamPage = () => {
    const dispatch = useAppDispatch();

    // Selectors
    const staffList = useAppSelector(selectStaffList);
    const isLoading = useAppSelector(selectIsLoading);
    const error = useAppSelector(selectError);
    const pagination = useAppSelector(selectPagination);
    const filters = useAppSelector(selectFilters);
    const selectedStaff = useAppSelector(selectSelectedStaff);
    const isCreateModalOpen = useAppSelector(selectIsCreateModalOpen);
    const isEditModalOpen = useAppSelector(selectIsEditModalOpen);
    const isDeleteModalOpen = useAppSelector(selectIsDeleteModalOpen);
    const isLeavePlanModalOpen = useAppSelector(selectIsLeavePlanModalOpen);
    const staffToDelete = useAppSelector(selectStaffToDelete);
    const operationSuccess = useAppSelector(selectOperationSuccess);
    const isRefreshing = useAppSelector(selectIsRefreshing);

    const [searchInput, setSearchInput] = useState('');

    // Pre-load roles so they're ready when the wizard opens.
    useEffect(() => {
        dispatch(fetchStaffRoles());
    }, [dispatch]);

    // Load staff list on mount and when filters/pagination change
    useEffect(() => {
        dispatch(fetchStaffList({
            page: pagination.page,
            size: pagination.size,
            activeOnly: filters.activeOnly,
            search: filters.search,
        }));
    }, [dispatch, pagination.page, pagination.size, filters.activeOnly, filters.search]);

    // Clear success message after 3 seconds and refetch data
    useEffect(() => {
        if (operationSuccess) {
            // Refetch staff list immediately after successful operation
            dispatch(fetchStaffList({
                page: pagination.page,
                size: pagination.size,
                activeOnly: filters.activeOnly,
                search: filters.search,
            }));

            // Clear success message after delay
            const timer = setTimeout(() => {
                dispatch(clearOperationSuccess());
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [operationSuccess, dispatch, pagination.page, pagination.size, filters.activeOnly, filters.search]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== filters.search) {
                dispatch(setFilters({ search: searchInput }));
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, dispatch, filters.search]);

    const handleEdit = useCallback((staff) => {
        dispatch(fetchStaff(staff.uuid)).then(() => {
            // Open without payload so selectedStaff keeps the full DTO
            // loaded by fetchStaff (which includes profileUuid etc.)
            dispatch(openEditModal());
        });
    }, [dispatch]);

    const handleDeactivate = useCallback((staff) => {
        // Opens the existing confirm modal which, on confirm, dispatches the
        // deleteStaff thunk (soft-delete — sets active=false on the backend).
        dispatch(openDeleteModal(staff));
    }, [dispatch]);

    const handleActivate = useCallback((staff) => {
        // Reactivation is non-destructive — flip the flag directly. The
        // backend's PUT /staff/{uuid} accepts a partial body with `active`.
        dispatch(updateStaff({ uuid: staff.uuid, staffData: { active: true } }));
    }, [dispatch]);

    const handleLeavePlan = useCallback((staff) => {
        dispatch(openLeavePlanModal(staff));
    }, [dispatch]);

    const handleResendEmail = useCallback((uuid) => {
        dispatch(resendWelcomeEmail(uuid));
    }, [dispatch]);

    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    return (
        <PageShell
            title="Team Members"
            description="Manage your team and their permissions"
            icon={Users}
        >
            {/* Success Toast */}
            {operationSuccess && (
                <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl shadow-lg animate-slide-in">
                    <CheckCircle className="text-emerald-500" size={18} />
                    <p className="text-sm text-emerald-700">{operationSuccess}</p>
                </div>
            )}

            {/* Actions bar */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                            type="checkbox"
                            checked={filters.activeOnly}
                            onChange={(e) => dispatch(setFilters({ activeOnly: e.target.checked }))}
                            className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        />
                        Active only
                    </label>
                    <button
                        onClick={() => dispatch(openCreateModal())}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors"
                    >
                        <Plus size={18} />
                        Add Staff
                    </button>
                </div>
            </div>

            {/* Error display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Team table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden relative">
                {/* Refreshing overlay */}
                {isRefreshing && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-xl">
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                            <span className="text-sm text-gray-600 font-medium">Updating...</span>
                        </div>
                    </div>
                )}
                <table className="w-full">
                    <thead>
                    <tr className="border-b border-gray-100">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Member
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                            Role
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                            Profile
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                            UI View Profile
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                            Status
                        </th>
                        <th className="px-5 py-3 w-10"></th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                        <tr>
                            <td colSpan={6} className="px-5 py-12 text-center">
                                <Loader2 className="animate-spin mx-auto text-gray-400" size={24} />
                                <p className="mt-2 text-sm text-gray-500">Loading staff...</p>
                            </td>
                        </tr>
                    ) : staffList.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="px-5 py-12 text-center">
                                <Users className="mx-auto text-gray-300" size={40} />
                                <p className="mt-2 text-sm text-gray-500">
                                    {filters.search ? 'No staff members found matching your search' : 'No staff members yet'}
                                </p>
                            </td>
                        </tr>
                    ) : (
                        staffList.map((member) => (
                            <tr key={member.uuid} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                                                <span className="text-emerald-700 font-semibold text-sm">
                                                    {member.firstName?.[0]}{member.lastName?.[0]}
                                                </span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{member.fullName}</p>
                                            <p className="text-xs text-gray-500">{member.email}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-5 py-4 hidden sm:table-cell">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700">
                                            {member.role?.replace(/_/g, ' ')}
                                        </span>
                                </td>
                                <td className="px-5 py-4 hidden md:table-cell">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700">
                                            {member.profileName || 'No profile'}
                                        </span>
                                </td>
                                <td className="px-5 py-4 hidden md:table-cell">
                                    {member.uiViewProfileName ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700">
                                            {member.uiViewProfileName}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>
                                <td className="px-5 py-4 hidden lg:table-cell">
                                        <span className={`inline-flex items-center gap-1.5 text-sm ${member.active ? 'text-gray-600' : 'text-gray-400'}`}>
                                            <span className={`w-2 h-2 rounded-full ${member.active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                            {member.active ? 'Active' : 'Inactive'}
                                        </span>
                                </td>
                                <td className="px-5 py-4">
                                    <StaffRowMenu
                                        staff={member}
                                        onEdit={handleEdit}
                                        onActivate={handleActivate}
                                        onDeactivate={handleDeactivate}
                                        onLeavePlan={handleLeavePlan}
                                        onResendEmail={handleResendEmail}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            Showing {pagination.page * pagination.size + 1} to {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} staff
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={!pagination.hasPrevious}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {pagination.page + 1} of {pagination.totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={!pagination.hasNext}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <TeamMemberWizard
                isOpen={isCreateModalOpen}
                onClose={() => dispatch(closeCreateModal())}
                isEditing={false}
                onSaved={() => dispatch(fetchStaffList({
                    page: pagination.page,
                    size: pagination.size,
                    activeOnly: filters.activeOnly,
                    search: filters.search,
                }))}
            />
            <TeamMemberWizard
                isOpen={isEditModalOpen}
                onClose={() => dispatch(closeEditModal())}
                staff={selectedStaff}
                isEditing={true}
                onSaved={() => dispatch(fetchStaffList({
                    page: pagination.page,
                    size: pagination.size,
                    activeOnly: filters.activeOnly,
                    search: filters.search,
                }))}
            />
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => dispatch(closeDeleteModal())}
                staff={staffToDelete}
            />
            <LeavePlanModal
                isOpen={isLeavePlanModalOpen}
                onClose={() => dispatch(closeLeavePlanModal())}
                staff={selectedStaff}
            />
        </PageShell>
    );
};

export default TeamPage;